import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CheckCircle2, Table, ArrowLeft, Clock, ShieldCheck,
  FileText, Download, UserCheck, Info, Sparkles, Key,
  TrendingUp, Award, DollarSign, Percent, Check,
  Calculator as CalcIcon, X
} from 'lucide-react';
import Calculator from '../components/Calculator';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../Firebase';

export const defaultLoanProducts = [
  {
    id: 'gold',
    name: 'Agriculture Gold Loan',
    tagline: 'Instant cash credit locked against gold ornaments with special agricultural discounts',
    rate: 'Starts 9.25% p.a.',
    numericRate: 9.25,
    maxAmount: 'Up to ₹20 Lakhs',
    numericMin: 10000,
    numericMax: 2000000,
    defaultTenure: 12,
    processingTime: '45 Mins',
    tenureOptions: '12 Months to 36 Months',
    overview: 'Our Agriculture Gold Loan is designed for members who need immediate cash flows for agricultural inputs, crop seeds, or household emergencies. We evaluate your gold ornaments on-site and disburse credit directly to your ledger in under 45 minutes.',
    eligibility: [
      'Farmer members holding registered agricultural land extracts.',
      'Individual co-members possessing physical hallmarked gold ornaments.',
      'Applicants aged 18 to 70 years.'
    ],
    purposes: [
      'Purchase of crop seeds, fertilizers, and pesticide spray kits.',
      'Emergency financing for agricultural machinery repairs.',
      'Immediate personal, medical, or family credit requirements.'
    ],
    documents: ['Aadhar Card', 'PAN Card', 'Land Record (7/12 Extract)', 'Assayed Gold Ornaments'],
    collateral: 'Physical gold ornaments (minimum 18-carat purity). Valued on-site by bank-certified appraisers.',
    feeDetails: '0.50% processing fee. No hidden valuation fees. GST applicable.',
    repaymentMethods: ['Harvest-linked schedules', 'Bullet payments at term end', 'Auto-debit from Savings ledger'],
    charges: [
      { type: 'Processing Fee', fee: '0.50% of loan size', desc: 'Charged at disbursement' },
      { type: 'Gold Valuation', fee: '₹250 - ₹500', desc: 'Flat fee for appraiser verification' },
      { type: 'Late EMI Fine', fee: '2.00% per month', desc: 'Applied on overdue principal amount' }
    ],
    faqs: [
      { q: 'What gold purity is accepted?', a: 'We accept gold jewelry of 18-carat to 24-carat purity. Bars and coins are subject to regulatory cooperative limits.' },
      { q: 'How long does valuation take?', a: 'Valuation is completed at our branch counters in approximately 15 to 30 minutes.' }
    ]
  },
  {
    id: 'personal',
    name: 'Personal Loan',
    tagline: 'Quick unsecured credit to cover weddings, home upgrades, and medical costs',
    rate: 'Starts 10.50% p.a.',
    numericRate: 10.50,
    maxAmount: 'Up to ₹5 Lakhs',
    numericMin: 50000,
    numericMax: 500000,
    defaultTenure: 36,
    processingTime: '24 Hours',
    tenureOptions: '12 Months to 60 Months',
    overview: 'Need capital for an unexpected emergency, medical expense, or family wedding? Our unsecured Personal Loan offers flexible repayments, fast credit reviews, and competitive interest rates with minimal documentation.',
    eligibility: [
      'Salaried employees of government, municipal, or reputed private companies.',
      'Self-employed professionals and business owners with steady cashflows.',
      'Minimum net monthly income of ₹15,000.'
    ],
    purposes: [
      'Family weddings or social celebrations.',
      'Emergency medical treatment or hospitalization expenses.',
      'Home renovations, consumer durable purchases, or travel bookings.'
    ],
    documents: ['Identity/Address Proof', 'Latest 3 months salary slips', 'Bank Statements (6 months)', 'Form 16 / ITR'],
    collateral: 'No physical mortgage required. Requires one co-member guarantor from the society with active deposits.',
    feeDetails: '1.00% processing fee. GST extra. No documentation stamp charges.',
    repaymentMethods: ['ECS / NACH mandates', 'Automated Savings ledger debit', 'Net Banking online transfers'],
    charges: [
      { type: 'Processing Fee', fee: '1.00% of loan size', desc: 'Charged during documentation check' },
      { type: 'Prepayment Charge', fee: 'Zero', desc: 'No charges after 6 monthly EMIs are paid' },
      { type: 'Bounce Charges', fee: '₹350 per instance', desc: 'Applied for ECS/NACH bounce events' }
    ],
    faqs: [
      { q: 'Do I need to submit collateral?', a: 'No, this is an unsecured personal loan. We only require a guarantor signature.' },
      { q: 'What is the processing time?', a: 'Approval is generally given in 24 hours after verification of salary slips and banking history.' }
    ]
  },
  {
    id: 'home',
    name: 'Home Loan',
    tagline: 'Affordable mortgages to buy your dream flat or construct your family home',
    rate: 'Starts 8.90% p.a.',
    numericRate: 8.90,
    maxAmount: 'Up to ₹75 Lakhs',
    numericMin: 500000,
    numericMax: 7500000,
    defaultTenure: 180,
    processingTime: '5 Days',
    tenureOptions: '5 Years to 20 Years',
    overview: 'Purchase land, build your family home, or buy a flat in Sangli and other urban centers with Sahyadri Home Loans. We offer stable interest margins, simple land title clearances, and long-term repayment schedules.',
    eligibility: [
      'Individuals aged 21 to 65 years with a stable source of monthly income.',
      'Minimum credit score of 700 or strong deposit relationship with our society.',
      'Co-applicants (spouse/parents) allowed to enhance eligibility.'
    ],
    purposes: [
      'Purchase of residential apartments, villas, or row houses.',
      'Construction of home on privately owned plots of land.',
      'Major structural renovations, adding rooms, or home extensions.'
    ],
    documents: ['Approved Building Plan', 'Sale Agreement / Title Deed', 'Income Proofs (ITR/Salary)', 'Property Tax receipts'],
    collateral: 'Equitable mortgage of the residential property being financed. Clear title search certificates required.',
    feeDetails: '0.50% processing fee (capped at ₹25,000). Legal search and valuation fee actuals apply.',
    repaymentMethods: ['Automated Clearing House (NACH)', 'Standing Instructions on savings ledger', 'UPI / GPay payments'],
    charges: [
      { type: 'Processing Fee', fee: '0.50% of loan amount', desc: 'Capped at maximum of ₹25,000' },
      { type: 'Legal Search Fee', fee: 'Actuals (₹2,500 - ₹5,000)', desc: 'Paid to society-empaneled advocate' },
      { type: 'Foreclosure Fee', fee: 'Zero', desc: 'No prepayment penalty for floating interest mortgages' }
    ],
    faqs: [
      { q: 'What is the maximum loan tenure?', a: 'We offer flexible tenures up to 240 months (20 years) for residential property mortgages.' },
      { q: 'Is a co-applicant mandatory?', a: 'Yes, typically spouses or co-owners of the property must register as co-applicants.' }
    ]
  },
  {
    id: 'vehicle',
    name: 'Vehicle Loan',
    tagline: 'Drive home your new passenger car or two-wheeler with fast finance',
    rate: 'Starts 9.50% p.a.',
    numericRate: 9.50,
    maxAmount: 'Up to ₹15 Lakhs',
    numericMin: 100000,
    numericMax: 1500000,
    defaultTenure: 60,
    processingTime: '48 Hours',
    tenureOptions: '12 Months to 84 Months',
    overview: 'Buy a new car, scooter, or transport vehicle with up to 90% on-road financing. We offer immediate registration coordination, quick processing, and competitive interest rates.',
    eligibility: [
      'Salaried, professionals, or business owners holding valid driving licenses.',
      'Net monthly income exceeding ₹12,000.',
      'Applicants aged 21 to 60 years.'
    ],
    purposes: [
      'Purchase of brand new passenger cars or SUVs.',
      'Purchase of two-wheelers (scooters, gear bikes).',
      'Certified pre-owned cars (not older than 5 years).'
    ],
    documents: ['Vehicle Proforma Invoice', 'Driving License', 'ITR or Salary slip proofs', 'Address Verification'],
    collateral: 'Hypothecation of the vehicle being purchased. Registration Certificate (RC) must note society lien.',
    feeDetails: 'Flat ₹1,500 documentation fee. Legal actuals for RTO hypothecation endorsement.',
    repaymentMethods: ['Monthly ECS / NACH auto-debit', 'Post-dated checks (PDCs)', 'Branch cash desk deposit'],
    charges: [
      { type: 'Documentation Fee', fee: '₹1,500 flat', desc: 'Charged at loan sanction' },
      { type: 'RTO Endorsement', fee: 'Actuals', desc: 'Paid to vehicle dealer agent' },
      { type: 'Foreclosure Charge', fee: '1.00% of outstanding', desc: 'Applicable if closed within 1 year' }
    ],
    faqs: [
      { q: 'Do you finance pre-owned vehicles?', a: 'Yes, we finance certified pre-owned passenger vehicles up to 70% of valuation (max age 5 years).' },
      { q: 'What is the margin requirement?', a: 'We offer up to 90% financing on the on-road invoice price for new passenger cars.' }
    ]
  },
  {
    id: 'business',
    name: 'MSME Business Loan',
    tagline: 'Boost your trade stock, machinery purchase, and operational cash reserves',
    rate: 'Starts 9.75% p.a.',
    numericRate: 9.75,
    maxAmount: 'Up to ₹30 Lakhs',
    numericMin: 200000,
    numericMax: 3000000,
    defaultTenure: 36,
    processingTime: '72 Hours',
    tenureOptions: '12 Months to 60 Months',
    overview: 'Expand your retail shop, add factory machinery, or manage seasonal inventory lags with Sahyadri MSME Business Loans. We offer collateral-free limits for qualifying firms.',
    eligibility: [
      'Registered MSMEs, traders, retailers, or fabricators with active business vintage of 2+ years.',
      'GST registration and regular tax filings.',
      'Minimum business turnover of ₹10 Lakhs p.a.'
    ],
    purposes: [
      'Working capital reserves to balance cash flow cycles.',
      'Purchase of factory machinery, computers, generators, or office furniture.',
      'Inventory restocking for seasonal trade demand.'
    ],
    documents: ['Udyam Registration', 'Shop Act License', 'GST returns (1 year)', 'Partnership deed/Ownership papers'],
    collateral: 'Hypothecation of business plant & machinery or stock. Property mortgage required for limits exceeding ₹10 Lakhs.',
    feeDetails: '0.75% processing fee. Actual charges apply for legal verification of business assets.',
    repaymentMethods: ['Electronic Clearing Service (ECS)', 'Net Banking corporate transfers', 'Auto-debit from current account'],
    charges: [
      { type: 'Processing Fee', fee: '0.75% of sanctioned limit', desc: 'Charged during loan disbursal' },
      { type: 'Asset Valuation', fee: 'Actuals', desc: 'Applicable for plant & machinery checks' },
      { type: 'Overdue Penalty', fee: '2.00% extra per month', desc: 'Applied on late installment values' }
    ],
    faqs: [
      { q: 'Can I get a loan without a property mortgage?', a: 'Yes, working capital limits up to ₹10 Lakhs are available against machinery hypothecation.' },
      { q: 'What business proof is accepted?', a: 'Udyam Registration, Shop Act License, GST registrations, and Partnership deeds are accepted.' }
    ]
  },
  {
    id: 'agri',
    name: 'Agriculture Loan (Kharif/Machinery)',
    tagline: 'Finance crop seeds, tractors, and micro-irrigation lines at specialized cooperative rates',
    rate: 'Starts 7.50% p.a.',
    numericRate: 7.50,
    maxAmount: 'Up to ₹10 Lakhs',
    numericMin: 25000,
    numericMax: 1000000,
    defaultTenure: 12,
    processingTime: '3 Days',
    tenureOptions: '12 Months to 60 Months',
    overview: 'Dedicated credit lines for crop inputs, farm tools, solar water pumps, or purchase of dairy cattle. Designed exclusively for cooperative farmer co-members.',
    eligibility: [
      'Farmer members holding registered 7/12 & 8A land records in Maharashtra.',
      'Clear title of agricultural land ownership free of previous bank charges.'
    ],
    purposes: [
      'Purchase of crop seeds, chemical fertilizers, and organic manures.',
      'Financing farm machinery like tractors, rotavators, or seed drills.',
      'Setting up drip irrigation networks or digging farm ponds.'
    ],
    documents: ['7/12 & 8A Land extracts', 'No Dues Certificate', 'Project report for farm machinery buying'],
    collateral: 'Charge on agricultural land (via 7/12 endorsement) or hypothecation of the machinery purchased.',
    feeDetails: 'Subsidy eligible processing fees. Nominal stamp paper actuals.',
    repaymentMethods: ['Harvest-linked installments (Half-yearly)', 'Bullet repayment post-harvest', 'Standing instructions'],
    charges: [
      { type: 'Sanction Fee', fee: '0.25% flat', desc: 'Special discounted rate for farmer members' },
      { type: 'Land Registration Charge', fee: 'Actuals', desc: 'Paid to local government sub-registrar' },
      { type: 'No Dues Certificate Check', fee: 'Zero', desc: 'No charges for cooperative checks' }
    ],
    faqs: [
      { q: 'Is repayment flexible based on harvest seasons?', a: 'Yes, we structure payments in half-yearly intervals aligning with Kharif and Rabi harvest cycles.' },
      { q: 'How do I claim interest subsidies?', a: 'We directly submit claim logs to government departments for eligible farm subsidy payouts.' }
    ]
  }
];

export default function Loans() {
  const location = useLocation();
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [calcPrincipal, setCalcPrincipal] = useState(500000);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: '',
    amount: '',
    income: '',
    loanType: 'Personal Loan'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'loan_enquiries'), {
        ...formData,
        status: 'New',
        createdAt: new Date().toISOString()
      });
    } catch (err) {
      console.warn('Could not save enquiry to Firestore:', err);
    }
    alert(`Thank you, ${formData.name}! Your application for a ${formData.loanType} of amount ₹${parseFloat(formData.amount).toLocaleString('en-IN')} has been submitted. Our executive will reach out within 24 hours.`);
    setIsModalOpen(false);
    setFormData({
      name: '',
      contact: '',
      email: '',
      city: '',
      amount: '',
      income: '',
      loanType: 'Personal Loan'
    });
  };
  const [calcTenure, setCalcTenure] = useState(60); // in months
  const [calcRate, setCalcRate] = useState(10.5);
  const [eligIncome, setEligIncome] = useState(45000);
  const [eligEmi, setEligEmi] = useState(5000);
  const [eligTenure, setEligTenure] = useState(120); // in months
  const [faqOpen, setFaqOpen] = useState({});

  // Dynamic Firestore sync for loan products
  const [activeLoans, setActiveLoans] = useState(defaultLoanProducts);

  useEffect(() => {
    const fetchCustomLoans = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'loan_products'));
        const fbLoans = [];
        querySnapshot.forEach((doc) => {
          fbLoans.push({ id: doc.id, ...doc.data() });
        });
        const merged = [...defaultLoanProducts];
        if (fbLoans.length > 0) {
          fbLoans.forEach((fbL) => {
            const idx = merged.findIndex(l => l.id === fbL.id);
            if (idx > -1) { merged[idx] = fbL; } else { merged.push(fbL); }
          });
        }
        setActiveLoans(merged);
      } catch (err) {
        console.warn('Could not load dynamic loans from Firestore:', err);
        setActiveLoans(defaultLoanProducts);
      }
    };
    fetchCustomLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hash-based routing from drop-down menus reactively using React Router location
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const found = activeLoans.find(l => l.id === id);
      if (found) {
        setSelectedLoan(found);
        setCalcPrincipal(found.numericMin || 100000);
        setCalcRate(found.numericRate);
        setCalcTenure(found.defaultTenure || 60);
      } else {
        setSelectedLoan(null);
      }
    } else {
      setSelectedLoan(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.pathname, activeLoans]);

  const selectLoan = (loan) => {
    setSelectedLoan(loan);
    setCalcPrincipal(loan.numericMin || 100000);
    setCalcRate(loan.numericRate);
    setCalcTenure(loan.defaultTenure || 60);
    window.location.hash = `#${loan.id}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSelection = () => {
    setSelectedLoan(null);
    window.location.hash = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 140;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const toggleFaq = (idx) => {
    setFaqOpen(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Loan EMI calculations
  const calculateEMIValue = () => {
    const P = calcPrincipal;
    const r = calcRate / 12 / 100;
    const n = calcTenure;
    if (r === 0) return { emi: P / n, interest: 0, total: P };

    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    const totalInterest = totalPayment - P;

    return {
      emi: Math.round(emi),
      interest: Math.round(totalInterest),
      total: Math.round(totalPayment)
    };
  };

  // Loan Eligibility math
  const calculateEligibilityValue = () => {
    const maxEmiPossible = eligIncome * 0.5 - eligEmi;
    if (maxEmiPossible <= 0) return { loanAmount: 0, emi: 0 };
    const r = (selectedLoan?.numericRate || 10.5) / 12 / 100;
    const n = eligTenure;
    const loanAmount = (maxEmiPossible * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));

    return {
      loanAmount: Math.round(loanAmount),
      emi: Math.round(maxEmiPossible)
    };
  };

  const emiRes = calculateEMIValue();
  const eligRes = calculateEligibilityValue();

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {selectedLoan ? (
        /* FULL DETAILED SCROLLING VIEW FOR LOANS */
        <div className="animate-fadeIn pb-24 font-poppins">

          {/* Back Navigation Bar */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={clearSelection}
              className="inline-flex items-center space-x-2 text-xs font-bold text-[#B31825] uppercase tracking-wider hover:underline bg-white border border-gray-200 px-4 py-2.5 rounded-xl shadow-sm transition-all hover:scale-102"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Loans Portal</span>
            </button>
          </div>

          {/* 1. HERO SECTION */}
          <section id="hero" className="bg-gradient-to-r from-navy to-[#031d42] text-white py-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(179,24,37,0.1),transparent)] pointer-events-none" />
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              <div className="lg:col-span-8 space-y-4">
                <span className="text-xs font-bold text-golden uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full w-fit block">
                  Flexible Rates
                </span>
                <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                  {selectedLoan.name}
                </h1>
                <p className="text-gray-300 text-sm sm:text-base font-light leading-relaxed max-w-2xl">
                  {selectedLoan.tagline}
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, loanType: selectedLoan.name }));
                      setIsModalOpen(true);
                    }}
                    className="px-6 py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md font-sans"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={() => scrollToSection('calculators')}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/15"
                  >
                    Calculate EMI
                  </button>
                </div>
              </div>

              {/* Specs Box */}
              <div className="lg:col-span-4 bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md space-y-4">
                <span className="text-[10px] uppercase font-bold text-golden tracking-wider block">Credit Quick Slabs</span>
                <div className="grid grid-cols-2 gap-4 text-xs font-sans">
                  <div>
                    <span className="text-gray-400 block text-[9px] uppercase font-bold">Interest rate</span>
                    <span className="text-white font-bold text-sm block font-mono">{selectedLoan.rate}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[9px] uppercase font-bold">Max Limit Size</span>
                    <span className="text-white font-bold text-sm block font-mono">{selectedLoan.maxAmount}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[9px] uppercase font-bold">Tenure Options</span>
                    <span className="text-white font-bold text-[10px] block">{selectedLoan.tenureOptions}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[9px] uppercase font-bold">Approval Speed</span>
                    <span className="text-white font-bold text-sm block font-mono">{selectedLoan.processingTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* STICKY TOP SUB-NAVIGATION BAR */}
          <div className="sticky top-20 z-40 bg-white border-b border-gray-200 py-3 shadow-sm select-none font-montserrat">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex space-x-6 overflow-x-auto no-scrollbar scroll-smooth whitespace-nowrap text-xs font-bold text-gray-500">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'highlights', label: 'Highlights' },
                { id: 'benefits-features', label: 'Benefits & Features' },
                { id: 'purpose-details', label: 'Loan Details' },
                { id: 'eligibility-docs', label: 'Eligibility & Documents' },
                { id: 'timeline', label: 'Sanction Timeline' },
                { id: 'calculators', label: 'Calculators' },
                { id: 'faqs-center', label: 'FAQs & Resources' }
              ].map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollToSection(sec.id)}
                  className="hover:text-[#B31825] transition-colors pb-1 border-b-2 border-transparent hover:border-[#B31825]"
                >
                  {sec.label}
                </button>
              ))}
            </div>
          </div>

          {/* 2. LOAN OVERVIEW */}
          <section id="overview" className="py-20 bg-white text-navy scroll-mt-36">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit block font-montserrat">About the Loan</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Detailed Loan Overview</h2>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  {selectedLoan.overview}
                </p>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  Our credit programs operate with maximum transparency. There are no hidden verification fees, and interest rates are calculated on a reducing balance basis daily. By offering structured tenures, we help our co-members plan their cash flows easily without any stress.
                </p>
              </div>

              {/* Image Section replacing Graphic illustration */}
              <div 
                onClick={() => setShowLightbox(true)}
                className="bg-white border border-gray-250 rounded-3xl overflow-hidden flex items-center justify-center aspect-video shadow-md cursor-pointer hover:shadow-lg hover:border-[#B31825] transition-all group"
              >
                <img 
                  src={selectedLoan.imageUrl || "/loan sch.png"} 
                  alt="Loan Scheme Banner" 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </div>
          </section>

          {/* 3. KEY HIGHLIGHTS */}
          <section id="highlights" className="py-20 bg-neutralBg text-navy border-t border-gray-200 scroll-mt-36">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Key Highlights</h2>
                <p className="text-gray-500 text-sm">Key features and lending standards at a glance.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { title: 'Interest Rate', val: selectedLoan.rate, icon: Percent },
                  { title: 'Loan Amount size', val: selectedLoan.maxAmount, icon: DollarSign },
                  { title: 'Tenure Options', val: selectedLoan.tenureOptions, icon: Clock },
                  { title: 'Processing speed', val: selectedLoan.processingTime, icon: ShieldCheck },
                  { title: 'Processing Fee', val: selectedLoan.feeDetails ? selectedLoan.feeDetails.split('.')[0] : '0.50% processing fee', icon: Award },
                  { title: 'EMI Facility', val: selectedLoan.emiFacility || 'Available (Flexible)', icon: Table },
                  { title: 'Prepayment Slabs', val: selectedLoan.prepaymentSlabs || 'No charges after 6 EMIs', icon: TrendingUp },
                  { title: 'Balance Transfer', val: selectedLoan.balanceTransfer || 'Supported from other banks', icon: Info }
                ].map((hl, idx) => {
                  const Icon = hl.icon;
                  return (
                    <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:border-[#B31825] hover:shadow-md transition-all">
                      <Icon className="h-5 w-5 text-[#B31825] mb-3" />
                      <span className="text-[10px] text-gray-400 block font-bold uppercase tracking-wider">{hl.title}</span>
                      <span className="text-sm font-bold text-navy mt-1 block">{hl.val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* 4. BENEFITS & FEATURES SECTION */}
          <section id="benefits-features" className="py-20 bg-white text-navy border-t border-gray-200 scroll-mt-36">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

              {/* Benefits */}
              <div className="space-y-8">
                <div className="text-center max-w-3xl mx-auto space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">Member Benefits</h3>
                  <p className="text-gray-500 text-sm">Key benefits designed specifically to assist cooperative account holders.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                  {(selectedLoan.benefits && selectedLoan.benefits.length > 0 ? selectedLoan.benefits : [
                    'Minimal verification paperwork & hassle-free clearance procedures.',
                    'Interest margins applied strictly on reduce-balances daily.',
                    'Guaranteed security vault audits and appraiser certification checks.',
                    'Direct credits dispatched to savings account profiles inside 24 hours.'
                  ]).map((ben, idx) => (
                    <div key={idx} className="flex bg-[#F8FAFC] border border-gray-250 p-6 rounded-2xl shadow-sm items-start">
                      <span className="p-2 bg-emerald/10 text-emerald rounded-lg mr-4 shrink-0">
                        <Check className="h-4 w-4" />
                      </span>
                      <p className="text-gray-600 text-xs sm:text-sm font-light leading-relaxed">{ben}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-8 pt-12 border-t border-gray-100">
                <div className="text-center max-w-3xl mx-auto space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy font-montserrat">Premium Slabs & Features</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {(selectedLoan.features && selectedLoan.features.length > 0 ? selectedLoan.features : [
                    { title: 'Reducing Balances', desc: 'Interest calculations adjust daily, saving thousands on long terms.' },
                    { title: 'Guarantor Backup', desc: 'No collateral needed for minor sizes with society co-member approvals.' },
                    { title: 'Subsidies Sync', desc: 'Direct verification for government agro schemes and interest subsidies.' }
                  ]).map((feat, idx) => (
                    <div key={idx} className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-6 hover:border-[#B31825] transition-all">
                      <Sparkles className="h-5 w-5 text-[#B31825] mb-3" />
                      <h4 className="font-montserrat font-extrabold text-sm text-[#052046] mb-1">{feat.title}</h4>
                      <p className="text-gray-400 text-xs leading-normal font-light">{feat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </section>

          {/* 5. PURPOSE OF LOAN & LOAN DETAILS */}
          <section id="purpose-details" className="py-20 bg-neutralBg text-navy border-t border-gray-200 scroll-mt-36">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

              {/* Purposes */}
              <div className="space-y-8">
                <div className="text-center max-w-3xl mx-auto space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">Purpose of Loan</h3>
                  <p className="text-gray-500 text-sm">Permitted uses for funds disbursed under this credit line.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {selectedLoan.purposes.map((purp, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-all text-xs sm:text-sm text-gray-500 font-light font-poppins">
                      <span className="text-[#B31825] font-black text-sm block mb-1">#{idx + 1}</span>
                      <span>{purp}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amount Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto pt-8 border-t border-gray-150">
                <div className="bg-white border border-gray-200 rounded-3xl p-6 space-y-3">
                  <h4 className="font-montserrat font-bold text-sm text-[#052046]">Loan Size Parameters</h4>
                  <div className="space-y-2 text-xs font-poppins font-light text-gray-500">
                    <div className="flex justify-between border-b pb-1">
                      <span>Minimum Size</span>
                      <span className="font-bold text-navy font-mono">₹{selectedLoan.numericMin.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span>Maximum Size</span>
                      <span className="font-bold text-navy font-mono">{selectedLoan.maxAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Multiples of</span>
                      <span className="font-bold text-navy font-mono">{selectedLoan.loanMultiples || '₹5,000'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-3xl p-6 space-y-3">
                  <h4 className="font-montserrat font-bold text-sm text-[#052046]">Repayment Tenure Limits</h4>
                  <div className="space-y-2 text-xs font-poppins font-light text-gray-500">
                    <div className="flex justify-between border-b pb-1">
                      <span>Minimum Tenure</span>
                      <span className="font-bold text-navy font-mono">{selectedLoan.minTenure || '6 Months'}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span>Maximum Tenure</span>
                      <span className="font-bold text-navy font-mono">{selectedLoan.tenureOptions.split('to')[1] || selectedLoan.tenureOptions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Extension Option</span>
                      <span className="font-bold text-emerald">{selectedLoan.tenureExtension || 'Available (subject to clearance)'}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* 6. INTEREST SLABS & REPAYMENT OPTIONS */}
          <section id="interest-repayment" className="py-20 bg-white text-navy border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

              {/* Interest table */}
              <div className="max-w-4xl mx-auto space-y-6">
                <div className="text-center space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">Interest Rates Table</h3>
                  <p className="text-gray-500 text-xs">Standard slabs updated as per co-operative central bank audit directives.</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm font-sans">
                    <thead className="bg-[#052046] text-white font-montserrat uppercase tracking-wider text-[9px] sm:text-xs">
                      <tr>
                        <th className="px-6 py-4.5 font-bold">Applicant Category</th>
                        <th className="px-6 py-4.5 font-bold">Interest Rate Slabs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150 text-gray-700 font-poppins font-light">
                      <tr className="hover:bg-white transition-colors">
                        <td className="px-6 py-4 font-bold text-navy">Subsidized Farmers & Women Co-members</td>
                        <td className="px-6 py-4 font-bold text-emerald">{selectedLoan.numericRate.toFixed(2)}% p.a.</td>
                      </tr>
                      <tr className="hover:bg-white transition-colors">
                        <td className="px-6 py-4 font-bold text-navy">General Public Members</td>
                        <td className="px-6 py-4 font-medium">{(selectedLoan.numericRate + 0.5).toFixed(2)}% p.a.</td>
                      </tr>
                      <tr className="hover:bg-white transition-colors">
                        <td className="px-6 py-4 font-bold text-navy">Senior Citizens (Age 60+)</td>
                        <td className="px-6 py-4 font-bold text-emerald">{(selectedLoan.numericRate - 0.25).toFixed(2)}% p.a.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* EMI Payout Types */}
              <div className="space-y-8 pt-12 border-t border-gray-100">
                <div className="text-center max-w-3xl mx-auto space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">EMI Repayment Options</h3>
                  <p className="text-gray-500 text-xs">Choose the installment option that aligns with your regular income streams.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto font-sans">
                  {(selectedLoan.emiOptions && selectedLoan.emiOptions.length > 0 ? selectedLoan.emiOptions : [
                    { title: 'Standard Monthly EMI', desc: 'Equal monthly ledger adjustments covering principal and reduce-rate interest.' },
                    { title: 'Flexible EMI', desc: 'Adjust payment dates in accordance with your crop harvest months.' },
                    { title: 'Step-Up Options', desc: 'Start with small monthly EMIs that step up as business margins grow.' },
                    { title: 'Bullet Repayment', desc: 'Clear the entire principal lump-sum at term end; pay interest quarterly.' }
                  ]).map((opt, idx) => (
                    <div key={idx} className="bg-[#F8FAFC] border border-gray-250 p-5 rounded-2xl space-y-2 hover:border-[#B31825] transition-all">
                      <h4 className="font-montserrat font-extrabold text-sm text-navy">{opt.title}</h4>
                      <p className="text-gray-400 text-xs leading-normal font-light">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </section>

          {/* 7. ELIGIBILITY, COLLATERAL & DOCUMENTS */}
          <section id="eligibility-docs" className="py-20 bg-neutralBg text-navy border-t border-gray-200 scroll-mt-36">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

              {/* Eligibility & Collateral */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
                {/* Eligibility checklist */}
                <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 space-y-5">
                  <h4 className="font-montserrat font-extrabold text-base text-navy flex items-center">
                    <UserCheck className="h-5.5 w-5.5 mr-3 text-[#B31825]" />
                    <span>Eligibility Slabs</span>
                  </h4>
                  <ul className="space-y-3 text-xs sm:text-sm text-gray-500 font-light">
                    {selectedLoan.eligibility.map((elig, idx) => (
                      <li key={idx} className="flex items-start bg-[#F8FAFC] p-3 rounded-xl border border-gray-150">
                        <span className="text-emerald mr-3 font-bold">✓</span>
                        <span>{elig}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Collateral description */}
                <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 space-y-5">
                  <h4 className="font-montserrat font-extrabold text-base text-navy flex items-center">
                    <Key className="h-5.5 w-5.5 mr-3 text-[#B31825]" />
                    <span>Accepted Security & Collateral</span>
                  </h4>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light font-sans">{selectedLoan.collateral}</p>
                  <div className="bg-primary/5 p-4 rounded-xl border border-primary/10 text-[10px] text-gray-400 leading-normal font-light">
                    *Note: Equitable registry searches are empaneled with local advocates to clear titles before loan disbursement.
                  </div>
                </div>
              </div>

              {/* Documents Required */}
              <div className="space-y-8 pt-12 border-t border-gray-150">
                <div className="text-center max-w-3xl mx-auto space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">Required Documents Folder</h3>
                  <p className="text-gray-500 text-sm">Please carry original verification copies along with self-attested prints.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto font-poppins text-xs text-gray-500 font-light">
                  {(selectedLoan.documentCategories && selectedLoan.documentCategories.length > 0 ? selectedLoan.documentCategories : [
                    { title: 'Identity Proofs', desc: 'PAN Card, Aadhaar Card, Passport, or Voter Identification.' },
                    { title: 'Address Proofs', desc: 'Utility bills, Electricity bills, or Municipal tax receipts.' },
                    { title: 'Income Proofs', desc: 'Salary sheets (3 months) or Income Tax Return records (ITR).' },
                    { title: 'Lien Documents', desc: 'Agro 7/12 land logs, vehicle proforma invoice, or gold weight slips.' }
                  ]).map((docItem, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 p-5 rounded-2xl space-y-3 hover:border-[#B31825] transition-all">
                      <FileText className="h-5 w-5 text-[#B31825]" />
                      <h5 className="font-montserrat font-bold text-xs text-navy">{docItem.title}</h5>
                      <p className="text-[11px] text-gray-400">{docItem.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </section>

          {/* 8. SANCTION PROCESSING TIMELINE */}
          <section id="timeline" className="py-20 bg-white text-navy border-t border-gray-200 scroll-mt-36 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-[#B31825] font-montserrat font-bold text-xs uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Roadmap</span>
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Sanction & Processing Timeline</h2>
                <p className="text-gray-500 text-sm">Disbursal steps from submission to ledger credit.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-5xl mx-auto text-center relative z-10 font-poppins">
                {(selectedLoan.sanctionTimeline && selectedLoan.sanctionTimeline.length > 0 ? selectedLoan.sanctionTimeline : [
                  { title: 'Application', desc: 'Register online or submit files offline at branch desks.' },
                  { title: 'Verification', desc: 'Executives check coordinates, addresses, and physical gold/property titles.' },
                  { title: 'Assessment', desc: 'Credit check and risk assessments run on reducing ledgers.' },
                  { title: 'Approval', desc: 'Credit board signs off legal clearances and RTO hypothecation.' },
                  { title: 'Disbursal', desc: 'Limits dispatched directly into your savings ledger.' }
                ]).map((st, idx) => (
                  <div key={idx} className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-5 relative group hover:border-[#B31825] transition-all">
                    <span className="text-2xl font-black text-[#B31825]/20 block mb-2 font-mono group-hover:text-[#B31825] transition-colors">{String(idx + 1).padStart(2, '0')}</span>
                    <h4 className="font-montserrat font-extrabold text-sm text-[#052046] mb-1">{st.title}</h4>
                    <p className="text-[10px] text-gray-400 leading-normal font-light">{st.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 9. CALCULATORS SECTION (EMI & ELIGIBILITY SIDE-BY-SIDE) */}
          <section id="calculators" className="py-20 bg-[#052046] text-white border-t border-white/5 scroll-mt-36 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-xs font-bold text-golden uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full w-fit mx-auto block">Planner Center</span>
                <h2 className="font-montserrat font-extrabold text-3xl">Interactive Financial Planner Dashboard</h2>
                <p className="text-gray-300 text-sm">Pre-configure your loan repayments and check maximum eligibility sizes.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

                {/* 1. EMI CALCULATOR */}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 space-y-6">
                  <h3 className="font-montserrat font-extrabold text-lg text-golden flex items-center border-b border-white/10 pb-3">
                    <CalcIcon className="h-5 w-5 mr-2 text-golden" />
                    <span>EMI Installment Calculator</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Loan Size Required</span>
                        <span className="font-bold text-golden font-mono">₹{calcPrincipal.toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min={selectedLoan.numericMin}
                        max={selectedLoan.numericMax}
                        step="5000"
                        value={calcPrincipal}
                        onChange={(e) => setCalcPrincipal(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Annual Interest Rate</span>
                        <span className="font-bold text-golden font-mono">{calcRate}%</span>
                      </div>
                      <input
                        type="range"
                        min="5"
                        max="20"
                        step="0.1"
                        value={calcRate}
                        onChange={(e) => setCalcRate(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Repayment Term (Months)</span>
                        <span className="font-bold text-golden font-mono">{calcTenure} M</span>
                      </div>
                      <input
                        type="range"
                        min="6"
                        max="240"
                        step="6"
                        value={calcTenure}
                        onChange={(e) => setCalcTenure(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="bg-[#020c1a]/60 border border-white/5 p-5 rounded-2xl space-y-4 font-mono text-xs">
                    <div className="flex justify-between">
                      <span>Monthly Installment</span>
                      <span className="font-extrabold text-sm text-[#00A86B]">₹{emiRes.emi.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2 text-gray-400">
                      <span>Interest Payout</span>
                      <span>₹{emiRes.interest.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Total Repayable</span>
                      <span className="text-white">₹{emiRes.total.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                {/* 2. ELIGIBILITY CALCULATOR */}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 space-y-6">
                  <h3 className="font-montserrat font-extrabold text-lg text-golden flex items-center border-b border-white/10 pb-3">
                    <UserCheck className="h-5 w-5 mr-2 text-golden" />
                    <span>Eligibility Calculator</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Net Monthly Income</span>
                        <span className="font-bold text-golden font-mono">₹{eligIncome.toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min="10000"
                        max="300000"
                        step="5000"
                        value={eligIncome}
                        onChange={(e) => setEligIncome(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Existing Monthly EMI Payments</span>
                        <span className="font-bold text-golden font-mono">₹{eligEmi.toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100000"
                        step="1000"
                        value={eligEmi}
                        onChange={(e) => setEligEmi(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Tenure Preference (Months)</span>
                        <span className="font-bold text-golden font-mono">{eligTenure} M</span>
                      </div>
                      <input
                        type="range"
                        min="12"
                        max="240"
                        step="12"
                        value={eligTenure}
                        onChange={(e) => setEligTenure(Number(e.target.value))}
                        className="w-full accent-[#B31825] cursor-pointer h-1.5 bg-white/10 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="bg-[#020c1a]/60 border border-white/5 p-5 rounded-2xl space-y-4 font-mono text-xs">
                    <div className="flex justify-between">
                      <span>Max Loan Eligible</span>
                      <span className="font-extrabold text-sm text-golden">
                        {eligRes.loanAmount > 0 ? `₹${eligRes.loanAmount.toLocaleString('en-IN')}` : 'Not Eligible'}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2 text-gray-400">
                      <span>Allowed Monthly EMI</span>
                      <span>₹{eligRes.emi.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Assumed Interest Rate</span>
                      <span>{selectedLoan.rate}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* 10. DOWNLOADS & RESOURCES CENTER */}
          <section id="faqs-center" className="py-20 bg-white text-navy border-t border-gray-200 scroll-mt-36">
            <div className="max-w-4xl mx-auto px-4 space-y-16">

              {/* Downloads */}
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy font-montserrat">Downloads Center</h3>
                  <p className="text-gray-500 text-xs">Save printable guidelines and forms before starting verification.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { name: 'Application Form PDF', desc: 'Standard sanction request file.' },
                    { name: 'Document Checklist', desc: 'Reference list of target paper prints.' },
                    { name: 'Terms Mandate PDF', desc: 'Cooperative guidelines booklet.' }
                  ].map((doc, idx) => (
                    <div key={idx} className="bg-[#F8FAFC] border border-gray-200 p-5 rounded-2xl flex flex-col justify-between hover:border-[#B31825] transition-all">
                      <div>
                        <h5 className="font-montserrat font-bold text-xs text-[#052046]">{doc.name}</h5>
                        <p className="text-gray-400 text-[10px] mt-1 font-light leading-relaxed">{doc.desc}</p>
                      </div>
                      <button className="inline-flex items-center space-x-2 text-[10px] font-bold text-[#B31825] uppercase tracking-wider hover:underline mt-4">
                        <Download className="h-3.5 w-3.5" />
                        <span>Download PDF</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs Accordions */}
              <div className="space-y-6 pt-12 border-t border-gray-150">
                <div className="text-center space-y-2">
                  <h3 className="font-montserrat font-extrabold text-2xl text-navy">FAQ Help Desk</h3>
                </div>
                <div className="space-y-3 max-w-3xl mx-auto">
                  {selectedLoan.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-[#F8FAFC] border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleFaq(idx)}
                        className="w-full flex justify-between items-center p-4 text-left font-montserrat font-bold text-xs sm:text-sm text-navy hover:text-[#B31825] transition-colors"
                      >
                        <span>{faq.q}</span>
                        <span className="text-xs text-gray-400 font-bold font-mono">{faqOpen[idx] ? '—' : '+'}</span>
                      </button>
                      {faqOpen[idx] && (
                        <div className="p-4 border-t border-gray-150 text-xs sm:text-sm text-gray-500 leading-relaxed font-light">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* T&C Bullet Card */}
              <div className="bg-[#F8FAFC] border border-gray-250 rounded-3xl p-6 sm:p-8 space-y-4">
                <h4 className="font-montserrat font-extrabold text-sm text-[#052046] flex items-center">
                  <Info className="h-4.5 w-4.5 mr-2 text-[#B31825]" />
                  <span>Important cooperative guidelines</span>
                </h4>
                <div className="max-h-40 overflow-y-auto pr-2 no-scrollbar text-xs text-gray-400 leading-relaxed font-light space-y-2 font-poppins">
                  {(selectedLoan.guidelines && selectedLoan.guidelines.length > 0 ? selectedLoan.guidelines : [
                    'All applicants must hold active membership shares in Sahyadri Urban Co-operative Credit Society Ltd.',
                    'Sanctions are strictly conditional on credit checks, property appraisals, and asset valuations by bank appraisers.',
                    'ECS/NACH mandate bounce events attract a penal check return charge of ₹350 per instance.',
                    'Late payments on monthly installments attract a penalty rate of 2.00% per month on the overdue principal amount.'
                  ]).map((gl, idx) => (
                    <p key={idx}>• {gl}</p>
                  ))}
                </div>
              </div>

            </div>
          </section>

          {/* 11. FINAL CALL TO ACTION (CTA) & CONTACT */}
          <section id="contact" className="bg-gradient-to-r from-navy to-navy-dark text-white py-16 px-4 relative scroll-mt-36 border-t border-white/5">
            <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
              <div className="space-y-3">
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl">Apply For Cooperative Financing Today</h2>
                <p className="text-gray-300 text-xs sm:text-sm font-light max-w-md mx-auto">
                  Sanctioning is fast and completely transparent. Apply online or consult our certified officers at the nearest branch.
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 pt-2 select-none">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, loanType: selectedLoan.name }));
                    setIsModalOpen(true);
                  }}
                  className="px-8 py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all font-sans"
                >
                  Apply for Loan
                </button>
                <Link
                  to="/contact"
                  className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/15"
                >
                  Contact Branch
                </Link>
              </div>

              {/* Contact info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-10 border-t border-white/10 max-w-3xl mx-auto text-left text-xs font-light text-gray-300">
                <div className="space-y-1">
                  <span className="font-bold text-golden block">Customer Support</span>
                  <span>1800-123-4567 (Toll Free)</span>
                  <span className="block">support@sahyadripat.com</span>
                </div>
                <div className="space-y-1">
                  <span className="font-bold text-golden block">Working Hours</span>
                  <span>Mon – Sat: 10:00 AM – 6:00 PM</span>
                  <span className="block">Sunday: Holiday</span>
                </div>
                <div className="space-y-1">
                  <span className="font-bold text-golden block">Head Office</span>
                  <span>Sahyadri Towers, Sangli Bypass Road, Sangli, Maharashtra</span>
                </div>
              </div>
            </div>
          </section>

          {/* 12. STICKY BOTTOM ACTION BAR */}
          <div className="fixed bottom-0 left-0 w-full z-45 bg-[#052046]/95 backdrop-blur-sm border-t border-white/10 py-3.5 px-4 shadow-xl select-none font-poppins">
            <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
              <span className="hidden sm:inline font-montserrat font-bold text-xs text-white tracking-wide">
                Sanctioning {selectedLoan.name}?
              </span>
              <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, loanType: selectedLoan.name }));
                    setIsModalOpen(true);
                  }}
                  className="px-4 py-2 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all font-sans"
                >
                  Apply Now
                </button>
                <button
                  onClick={() => scrollToSection('calculators')}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all border border-white/15"
                >
                  Calculate EMI
                </button>
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 bg-white text-navy font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all hover:bg-gray-100"
                >
                  Exit Profile
                </button>
              </div>
            </div>
          </div>

        </div>
      ) : (
        /* ORIGINAL PORTAL LIST/GRID VIEW */
        <div>
          {/* Page Header */}
          <section 
            className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/page header.png')" }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
            <div className="max-w-4xl mx-auto space-y-4 relative z-10">
              <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Financing Solutions</span>
              <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Flexible Loans for Every Financial Milestone</h1>
              <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
                Empowering your personal needs, agricultural goals, business expansions, and home ownership with competitive rates, fast approvals, and zero hidden charges.
              </p>
            </div>
          </section>

          {/* Loan Grid details */}
          <section className="py-24 bg-white text-navy font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Credit Products</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Affordable Lending Portfolios</h2>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed">
                  Review our structured loan categories, interest rates, and minimal document checkmarks.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {activeLoans.map((loan, i) => (
                  <div
                    key={i}
                    id={loan.id}
                    className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-6 sm:p-8 hover:bg-white hover:border-[#B31825] hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group"
                  >
                    <div className="space-y-6">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h3 className="font-montserrat font-extrabold text-lg sm:text-xl text-navy group-hover:text-[#B31825] transition-colors">{loan.name}</h3>
                          <span className="text-xs text-primary font-bold block mt-1 tracking-wide">{loan.maxAmount} maximum size</span>
                        </div>
                        <span className="bg-emerald/10 text-emerald text-xs font-extrabold px-3 py-1.5 rounded-full shrink-0">
                          {loan.rate}
                        </span>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-gray-150">
                        <span className="text-[10px] text-gray-400 block font-bold uppercase tracking-wider">Eligibility</span>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed font-light">
                          {Array.isArray(loan.eligibility) ? loan.eligibility.join(' ') : loan.eligibility}
                        </p>
                      </div>

                      <div className="space-y-3 pt-2">
                        <span className="text-xs text-gray-400 block font-bold uppercase tracking-wider flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-[#B31825]" /> Required Checklist
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-poppins text-xs text-gray-500">
                          {loan.documents.slice(0, 4).map((doc, dIdx) => (
                            <div key={dIdx} className="flex items-start bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm font-light">
                              <CheckCircle2 className="h-4 w-4 text-[#B31825] shrink-0 mr-2 mt-0.5" />
                              <span>{doc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="pt-8 grid grid-cols-2 gap-4">
                      <button
                        onClick={() => selectLoan(loan)}
                        className="text-center py-3 bg-[#052046] hover:bg-[#0c3164] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, loanType: loan.name }));
                          setIsModalOpen(true);
                        }}
                        className="text-center py-3 bg-white border border-[#B31825] text-[#B31825] hover:bg-[#B31825] hover:text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all font-sans"
                      >
                        Apply for Loan
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* EMI Calculator */}
          <section className="py-24 bg-neutralBg text-navy border-t border-gray-255">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
                <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Credit Planning</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">EMI Installment Calculator</h2>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed font-light">
                  Simulate loan sizes, interest rates, and repayments below before requesting disbursal.
                </p>
              </div>

              <Calculator />
            </div>
          </section>

          {/* Important instructions */}
          <section className="py-24 bg-white text-navy border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-4">
              <div className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-8 sm:p-10 shadow-lg space-y-6 relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full" />
                <h3 className="font-montserrat font-extrabold text-xl text-[#052046] flex items-center">
                  <ShieldCheck className="h-6 w-6 mr-3 text-[#B31825]" />
                  <span>Cooperative Loan Approval Guidelines</span>
                </h3>

                <div className="space-y-4 text-xs sm:text-sm text-gray-500 leading-relaxed font-light">
                  <div className="flex items-start bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-[#B31825] font-bold mr-3 select-none mt-0.5">•</span>
                    <span>Applicant must hold a registered membership account or share certificates in Sahyadri Urban Credit Society.</span>
                  </div>
                  <div className="flex items-start bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-[#B31825] font-bold mr-3 select-none mt-0.5">•</span>
                    <span>Loan disbursals will be credited directly to the customer savings account within the bank after successful KYC & document verification.</span>
                  </div>
                  <div className="flex items-start bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-[#B31825] font-bold mr-3 select-none mt-0.5">•</span>
                    <span>A guarantor (co-member of the society) with active deposits is mandatory for unsecured personal and gold loans exceeding ₹5 Lakhs.</span>
                  </div>
                  <div className="flex items-start bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-[#B31825] font-bold mr-3 select-none mt-0.5">•</span>
                    <span>Interest calculation is conducted on a daily reducing balance ledger, keeping borrowing costs highly competitive.</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* APPLICATION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 relative animate-fadeIn">
            
            {/* Modal Header */}
            <div className="bg-[#052046] text-white p-6 relative">
              <h3 className="font-montserrat font-extrabold text-lg text-white">Apply for Sahyadri Loan</h3>
              <p className="text-xs text-gray-300 mt-1 font-light">Fill out your details to get a fast sanction review.</p>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4 text-navy">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Contact Number</label>
                <input
                  type="tel"
                  name="contact"
                  required
                  placeholder="Enter 10-digit mobile"
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">City / Location</label>
                <input
                  type="text"
                  name="city"
                  required
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#052046] uppercase">Required Loan (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    required
                    placeholder="e.g. 200000"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#052046] uppercase">Monthly Income (₹)</label>
                  <input
                    type="number"
                    name="income"
                    required
                    placeholder="e.g. 25000"
                    value={formData.income}
                    onChange={handleInputChange}
                    className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Loan Type</label>
                <select
                  name="loanType"
                  value={formData.loanType}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                >
                  <option value="Agriculture Gold Loan">Agriculture Gold Loan</option>
                  <option value="Personal Loan">Personal Loan</option>
                  <option value="Home Loan">Home Loan</option>
                  <option value="Vehicle Loan">Vehicle Loan</option>
                  <option value="MSME Business Loan">MSME Business Loan</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {showLightbox && (
        <div 
          onClick={() => setShowLightbox(false)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white p-2 shadow-2xl transition-transform duration-300 scale-100 hover:scale-102">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }} 
              className="absolute top-4 right-4 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <img 
              src="/loan sch.png" 
              alt="Loan Scheme Banner Zoomed" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
